import { useState, useEffect } from 'react';
import massTextService from '../services/massTextService';
import clientHelperService from '../services/clientHelperService';
import { formatDate } from '../utils/dateUtils';

const MassText = () => {
    // State for mass texts
    const [massTexts, setMassTexts] = useState([]);
    const [groups, setGroups] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Modal states
    const [showMassTextModal, setShowMassTextModal] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteType, setDeleteType] = useState(''); // 'masstext' or 'group'
    
    // Form states
    const [editingMassText, setEditingMassText] = useState(null);
    const [editingGroup, setEditingGroup] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Active tab: 'messages' or 'groups'
    const [activeTab, setActiveTab] = useState('messages');
    
    // Mass text form data
    const [massTextForm, setMassTextForm] = useState({
        message: '',
        send_datetime: '',
        from_phone_number: '',
        recipient_type: 'clients', // 'clients' or 'group'
        client_ids: [],
        group_id: ''
    });
    
    // Phone numbers
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    
    // Group form data
    const [groupForm, setGroupForm] = useState({
        group_name: '',
        client_ids: [],
        opted_out_client_ids: []
    });
    
    // Track initial opted-out state to prevent opting back in
    const [initialOptedOutClientIds, setInitialOptedOutClientIds] = useState([]);
    
    // Search and filter
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    
    // Fetch data on mount
    useEffect(() => {
        fetchData();
    }, []);
    
    const fetchData = async () => {
        try {
            setLoading(true);
            setError('');
            const [massTextsData, groupsData, clientsData, phoneNumbersData] = await Promise.all([
                massTextService.fetchMassTexts(),
                massTextService.fetchGroups(),
                clientHelperService.fetchClients(),
                massTextService.fetchPhoneNumbers()
            ]);
            setMassTexts(massTextsData);
            setGroups(groupsData);
            setClients(clientsData);
            setPhoneNumbers(phoneNumbersData);
        } catch (err) {
            setError(`Failed to fetch data: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };
    
    // Mass Text handlers
    const openMassTextModal = (massText = null) => {
        if (massText) {
            setEditingMassText(massText);
            
            // Convert UTC datetime from backend to local datetime for the form
            // Backend stores in UTC, we need to display in local time
            const utcDate = new Date(massText.send_datetime);
            
            // Format for datetime-local input (YYYY-MM-DDTHH:mm)
            // This will automatically use the user's local timezone
            const localDateStr = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000)
                .toISOString()
                .slice(0, 16);
            
            setMassTextForm({
                message: massText.message,
                send_datetime: localDateStr,
                from_phone_number: massText.from_phone_number || '',
                recipient_type: massText.group_id ? 'group' : 'clients',
                client_ids: massText.client_ids || [],
                group_id: massText.group_id || ''
            });
        } else {
            setEditingMassText(null);
            setMassTextForm({
                message: '',
                send_datetime: '',
                from_phone_number: '',
                recipient_type: 'clients',
                client_ids: [],
                group_id: ''
            });
        }
        setError('');
        setShowMassTextModal(true);
    };
    
    const handleMassTextSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!massTextForm.message.trim()) {
            setError('Message is required');
            return;
        }
        if (!massTextForm.send_datetime) {
            setError('Send date/time is required');
            return;
        }
        
        // Check if date is in the past (compare in local time)
        const selectedDateTime = new Date(massTextForm.send_datetime);
        const now = new Date();
        if (selectedDateTime < now) {
            setError('Send date/time must be in the future');
            return;
        }
        
        if (massTextForm.recipient_type === 'clients' && massTextForm.client_ids.length === 0) {
            setError('Please select at least one client');
            return;
        }
        if (massTextForm.recipient_type === 'group' && !massTextForm.group_id) {
            setError('Please select a group');
            return;
        }
        
        try {
            setSubmitting(true);
            setError('');
            
            // Send the datetime as-is (local time) without timezone conversion
            // The backend will treat it as US/Eastern and convert to UTC
            const payload = {
                message: massTextForm.message.trim(),
                send_datetime: massTextForm.send_datetime + ':00', // Add seconds if not present
                from_phone_number: massTextForm.from_phone_number || undefined,
                client_ids: massTextForm.recipient_type === 'clients' ? massTextForm.client_ids : undefined,
                group_id: massTextForm.recipient_type === 'group' ? massTextForm.group_id : undefined
            };
            
            if (editingMassText) {
                await massTextService.updateMassText(editingMassText.text_id, payload);
            } else {
                await massTextService.createMassText(payload);
            }
            
            setShowMassTextModal(false);
            await fetchData();
        } catch (err) {
            setError(`Failed to ${editingMassText ? 'update' : 'create'} mass text: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };
    
    const handleDeleteMassText = async () => {
        try {
            setSubmitting(true);
            setError('');
            await massTextService.deleteMassText(selectedItem.text_id);
            setShowDeleteModal(false);
            setSelectedItem(null);
            await fetchData();
        } catch (err) {
            setError(`Failed to delete mass text: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };
    
    const handleSendNow = async (massText) => {
        if (!window.confirm(`Are you sure you want to send this mass text NOW to ${massText.total_recipients} recipients?\n\nMessage: ${massText.message.substring(0, 100)}...`)) {
            return;
        }
        
        try {
            setLoading(true);
            setError('');
            const result = await massTextService.sendMassTextNow(massText.text_id);
            
            // Show success message with statistics
            alert(`Mass text sent!\n\nSuccess: ${result.success_count}\nFailed: ${result.failed_count}\nTotal: ${result.total_recipients}`);
            
            // Refresh the data to show updated status
            await fetchData();
        } catch (err) {
            setError(`Failed to send mass text: ${err.response?.data?.detail || err.message}`);
        } finally {
            setLoading(false);
        }
    };
    
    // Group handlers
    const openGroupModal = (group = null) => {
        if (group) {
            setEditingGroup(group);
            const initialOptedOut = group.opted_out_client_ids || [];
            setGroupForm({
                group_name: group.group_name,
                client_ids: group.client_ids || [],
                opted_out_client_ids: initialOptedOut
            });
            setInitialOptedOutClientIds(initialOptedOut);
        } else {
            setEditingGroup(null);
            setGroupForm({
                group_name: '',
                client_ids: [],
                opted_out_client_ids: []
            });
            setInitialOptedOutClientIds([]);
        }
        setError('');
        setShowGroupModal(true);
    };
    
    const handleGroupSubmit = async (e) => {
        e.preventDefault();
        
        if (!groupForm.group_name.trim()) {
            setError('Group name is required');
            return;
        }
        
        try {
            setSubmitting(true);
            setError('');
            
            const payload = {
                group_name: groupForm.group_name.trim(),
                client_ids: groupForm.client_ids,
                opted_out_client_ids: groupForm.opted_out_client_ids
            };
            
            if (editingGroup) {
                await massTextService.updateGroup(editingGroup.group_id, payload);
            } else {
                await massTextService.createGroup(payload);
            }
            
            setShowGroupModal(false);
            await fetchData();
        } catch (err) {
            setError(`Failed to ${editingGroup ? 'update' : 'create'} group: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };
    
    const handleDeleteGroup = async () => {
        try {
            setSubmitting(true);
            setError('');
            await massTextService.deleteGroup(selectedItem.group_id);
            setShowDeleteModal(false);
            setSelectedItem(null);
            await fetchData();
        } catch (err) {
            setError(`Failed to delete group: ${err.response?.data?.detail || err.message}`);
        } finally {
            setSubmitting(false);
        }
    };
    
    const openDeleteModal = (item, type) => {
        setSelectedItem(item);
        setDeleteType(type);
        setError('');
        setShowDeleteModal(true);
    };
    
    const closeModals = () => {
        setShowMassTextModal(false);
        setShowGroupModal(false);
        setShowDeleteModal(false);
        setEditingMassText(null);
        setEditingGroup(null);
        setSelectedItem(null);
        setError('');
    };
    
    // Helper functions
    const toggleClientSelection = (clientId) => {
        if (activeTab === 'messages') {
            setMassTextForm(prev => ({
                ...prev,
                client_ids: prev.client_ids.includes(clientId)
                    ? prev.client_ids.filter(id => id !== clientId)
                    : [...prev.client_ids, clientId]
            }));
        } else {
            setGroupForm(prev => ({
                ...prev,
                client_ids: prev.client_ids.includes(clientId)
                    ? prev.client_ids.filter(id => id !== clientId)
                    : [...prev.client_ids, clientId]
            }));
        }
    };
    
    const toggleOptOut = (clientId) => {
        setGroupForm(prev => {
            // If client was initially opted out, don't allow opting back in
            if (initialOptedOutClientIds.includes(clientId) && prev.opted_out_client_ids.includes(clientId)) {
                return prev; // Was initially opted out and still is, do nothing
            }
            // Otherwise allow normal toggle behavior
            return {
                ...prev,
                opted_out_client_ids: prev.opted_out_client_ids.includes(clientId)
                    ? prev.opted_out_client_ids.filter(id => id !== clientId)
                    : [...prev.opted_out_client_ids, clientId]
            };
        });
    };
    
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'scheduled':
                return 'bg-warning text-dark';
            case 'sending':
                return 'bg-info text-white';
            case 'sent':
                return 'bg-success text-white';
            case 'failed':
                return 'bg-danger text-white';
            case 'cancelled':
                return 'bg-secondary text-white';
            default:
                return 'bg-secondary text-white';
        }
    };
    
    // Filter and sort mass texts
    const filteredMassTexts = massTexts.filter(mt => {
        const matchesSearch = mt.message.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || mt.status === statusFilter;
        return matchesSearch && matchesStatus;
    }).sort((a, b) => new Date(b.send_datetime) - new Date(a.send_datetime));
    
    // Filter groups
    const filteredGroups = groups.filter(g => 
        g.group_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    if (loading) {
        return (
            <div className="container mt-4">
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="container-fluid mt-4">
            <h2 className="mb-4">Mass Text Management</h2>
            
            {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError('')}></button>
                </div>
            )}
            
            {/* Tabs */}
            <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'messages' ? 'active' : ''}`}
                        onClick={() => setActiveTab('messages')}
                    >
                        <i className="bi bi-chat-dots me-2"></i>
                        Mass Texts
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeTab === 'groups' ? 'active' : ''}`}
                        onClick={() => setActiveTab('groups')}
                    >
                        <i className="bi bi-people me-2"></i>
                        Groups
                    </button>
                </li>
            </ul>
            
            {/* Messages Tab */}
            {activeTab === 'messages' && (
                <div>
                    <div className="row mb-3">
                        <div className="col-md-4">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search messages..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-3">
                            <select
                                className="form-select"
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                            >
                                <option value="all">All Statuses</option>
                                <option value="scheduled">Scheduled</option>
                                <option value="sending">Sending</option>
                                <option value="sent">Sent</option>
                                <option value="failed">Failed</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                        <div className="col-md-5 text-end">
                            <button
                                className="btn btn-primary"
                                onClick={() => openMassTextModal()}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Schedule New Mass Text
                            </button>
                        </div>
                    </div>
                    
                    {/* Mass Texts Table */}
                    <div className="table-responsive">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Message</th>
                                    <th>Send Date/Time</th>
                                    <th>From Number</th>
                                    <th>Recipients</th>
                                    <th>Status</th>
                                    <th>Created By</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredMassTexts.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center text-muted">
                                            No mass texts found
                                        </td>
                                    </tr>
                                ) : (
                                    filteredMassTexts.map((mt) => (
                                        <tr key={mt.text_id}>
                                            <td>
                                                <div className="text-truncate" style={{ maxWidth: '300px' }}>
                                                    {mt.message}
                                                </div>
                                            </td>
                                            <td>{formatDate(mt.send_datetime)}</td>
                                            <td>
                                                {mt.from_phone_number ? (
                                                    <span className="text-nowrap">
                                                        {mt.from_phone_number}
                                                    </span>
                                                ) : (
                                                    <span className="text-muted">
                                                        HFF
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {mt.group_id ? (
                                                    <span className="badge bg-info">
                                                        Group: {groups.find(g => g.group_id === mt.group_id)?.group_name || 'Unknown'}
                                                    </span>
                                                ) : (
                                                    <span className="badge bg-primary">
                                                        {mt.client_ids?.length || 0} clients
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={`badge ${getStatusBadgeClass(mt.status)}`}>
                                                    {mt.status.charAt(0).toUpperCase() + mt.status.slice(1)}
                                                </span>
                                                {mt.status === 'sent' && (
                                                    <div className="small text-muted mt-1">
                                                        {mt.success_count}/{mt.total_recipients} sent
                                                    </div>
                                                )}
                                            </td>
                                            <td>{mt.created_by}</td>
                                            <td>
                                                <div className="d-flex gap-1">
                                                    {mt.status === 'scheduled' && (
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => handleSendNow(mt)}
                                                            title="Send Now"
                                                        >
                                                            <i className="bi bi-send-fill"></i> Send Now
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => openMassTextModal(mt)}
                                                        title={mt.status === 'scheduled' ? 'Edit' : 'Reschedule'}
                                                    >
                                                        <i className="bi bi-pencil"></i> {mt.status === 'scheduled' ? 'Edit' : 'Reschedule'}
                                                    </button>
                                                    {(mt.status === 'sent' || mt.status === 'failed') && (
                                                        <button
                                                            className="btn btn-sm btn-success"
                                                            onClick={() => handleSendNow(mt)}
                                                            title="Send Again"
                                                        >
                                                            <i className="bi bi-arrow-repeat"></i> Send Again
                                                        </button>
                                                    )}
                                                    <button
                                                        className="btn btn-sm btn-outline-danger"
                                                        onClick={() => openDeleteModal(mt, 'masstext')}
                                                        title="Delete"
                                                    >
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            
            {/* Groups Tab */}
            {activeTab === 'groups' && (
                <div>
                    <div className="row mb-3">
                        <div className="col-md-6">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search groups..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className="col-md-6 text-end">
                            <button
                                className="btn btn-primary"
                                onClick={() => openGroupModal()}
                            >
                                <i className="bi bi-plus-circle me-2"></i>
                                Create New Group
                            </button>
                        </div>
                    </div>
                    
                    {/* Groups Cards */}
                    <div className="row">
                        {filteredGroups.length === 0 ? (
                            <div className="col-12">
                                <div className="alert alert-info">
                                    No groups found. Create a group to easily send mass texts to multiple clients.
                                </div>
                            </div>
                        ) : (
                            filteredGroups.map((group) => {
                                const activeClients = (group.client_ids?.length || 0) - (group.opted_out_client_ids?.length || 0);
                                return (
                                    <div key={group.group_id} className="col-md-6 col-lg-4 mb-3">
                                        <div className="card h-100">
                                            <div className="card-body">
                                                <h5 className="card-title">{group.group_name}</h5>
                                                <p className="card-text text-muted mb-1">
                                                    <i className="bi bi-people me-2"></i>
                                                    {group.client_ids?.length || 0} total clients
                                                </p>
                                                {group.opted_out_client_ids && group.opted_out_client_ids.length > 0 && (
                                                    <p className="card-text text-warning small mb-1">
                                                        <i className="bi bi-person-x me-1"></i>
                                                        {group.opted_out_client_ids.length} opted out
                                                    </p>
                                                )}
                                                <p className="card-text text-success small mb-2">
                                                    <i className="bi bi-check-circle me-1"></i>
                                                    {activeClients} active recipients
                                                </p>
                                                <p className="card-text small text-muted">
                                                    Created by: {group.created_by}
                                                </p>
                                            </div>
                                            <div className="card-footer bg-transparent">
                                                <button
                                                    className="btn btn-sm btn-outline-primary me-2"
                                                    onClick={() => openGroupModal(group)}
                                                >
                                                    <i className="bi bi-pencil me-1"></i>
                                                    Edit
                                                </button>
                                                <button
                                                    className="btn btn-sm btn-outline-danger"
                                                    onClick={() => openDeleteModal(group, 'group')}
                                                >
                                                    <i className="bi bi-trash me-1"></i>
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
            
            {/* Mass Text Modal */}
            {showMassTextModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingMassText 
                                        ? (editingMassText.status === 'scheduled' ? 'Edit Mass Text' : 'Reschedule Mass Text')
                                        : 'Schedule New Mass Text'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeModals}></button>
                            </div>
                            <form onSubmit={handleMassTextSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Message *</label>
                                        <textarea
                                            className="form-control"
                                            rows="4"
                                            value={massTextForm.message}
                                            onChange={(e) => setMassTextForm({ ...massTextForm, message: e.target.value })}
                                            maxLength="1600"
                                            required
                                        />
                                        <div className="form-text">
                                            {massTextForm.message.length}/1600 characters
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label">Send Date & Time (Local Time) *</label>
                                        <input
                                            type="datetime-local"
                                            className="form-control"
                                            value={massTextForm.send_datetime}
                                            onChange={(e) => setMassTextForm({ ...massTextForm, send_datetime: e.target.value })}
                                            min={new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                                            required
                                        />
                                        <div className="form-text">
                                            Enter the time in your local timezone. Schedule time must be in the future.
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label">From Phone Number</label>
                                        <select
                                            className="form-select"
                                            value={massTextForm.from_phone_number}
                                            onChange={(e) => setMassTextForm({ ...massTextForm, from_phone_number: e.target.value })}
                                        >
                                            <option value="">Default Sender ID (HFF)</option>
                                            {phoneNumbers.map((phone) => (
                                                <option key={phone.phone_number} value={phone.phone_number}>
                                                    {phone.phone_number} {phone.friendly_name ? `(${phone.friendly_name})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="form-text">
                                            Select a phone number to send from, or use the default sender ID
                                        </div>
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label">Recipients *</label>
                                        <div className="btn-group w-100 mb-3" role="group">
                                            <input
                                                type="radio"
                                                className="btn-check"
                                                name="recipient_type"
                                                id="recipient_clients"
                                                checked={massTextForm.recipient_type === 'clients'}
                                                onChange={() => setMassTextForm({ ...massTextForm, recipient_type: 'clients', group_id: '' })}
                                            />
                                            <label className="btn btn-outline-primary" htmlFor="recipient_clients">
                                                Select Clients
                                            </label>
                                            
                                            <input
                                                type="radio"
                                                className="btn-check"
                                                name="recipient_type"
                                                id="recipient_group"
                                                checked={massTextForm.recipient_type === 'group'}
                                                onChange={() => setMassTextForm({ ...massTextForm, recipient_type: 'group', client_ids: [] })}
                                            />
                                            <label className="btn btn-outline-primary" htmlFor="recipient_group">
                                                Select Group
                                            </label>
                                        </div>
                                        
                                        {massTextForm.recipient_type === 'clients' ? (
                                            <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem', padding: '0.5rem' }}>
                                                {clients.length === 0 ? (
                                                    <div className="text-muted text-center">No clients available</div>
                                                ) : (
                                                    clients.map((client) => (
                                                        <div key={client.client_id} className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                id={`client_${client.client_id}`}
                                                                checked={massTextForm.client_ids.includes(client.client_id)}
                                                                onChange={() => toggleClientSelection(client.client_id)}
                                                            />
                                                            <label className="form-check-label" htmlFor={`client_${client.client_id}`}>
                                                                {client.client_name}
                                                            </label>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        ) : (
                                            <select
                                                className="form-select"
                                                value={massTextForm.group_id}
                                                onChange={(e) => setMassTextForm({ ...massTextForm, group_id: e.target.value })}
                                                required={massTextForm.recipient_type === 'group'}
                                            >
                                                <option value="">Select a group...</option>
                                                {groups.map((group) => (
                                                    <option key={group.group_id} value={group.group_id}>
                                                        {group.group_name} ({group.client_ids?.length || 0} clients)
                                                    </option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                {editingMassText ? 'Updating...' : 'Scheduling...'}
                                            </>
                                        ) : (
                                            editingMassText 
                                                ? (editingMassText.status === 'scheduled' ? 'Update' : 'Reschedule')
                                                : 'Schedule'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Group Modal */}
            {showGroupModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editingGroup ? 'Edit Group' : 'Create New Group'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeModals}></button>
                            </div>
                            <form onSubmit={handleGroupSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Group Name *</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={groupForm.group_name}
                                            onChange={(e) => setGroupForm({ ...groupForm, group_name: e.target.value })}
                                            required
                                        />
                                    </div>
                                    
                                    <div className="mb-3">
                                        <label className="form-label">Clients</label>
                                        <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '0.25rem', padding: '0.5rem' }}>
                                            {clients.length === 0 ? (
                                                <div className="text-muted text-center">No clients available</div>
                                            ) : (
                                                <table className="table table-sm mb-0">
                                                    <thead>
                                                        <tr>
                                                            <th>Include</th>
                                                            <th>Client Name</th>
                                                            <th>Opted Out</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {clients.map((client) => {
                                                            const isSelected = groupForm.client_ids.includes(client.client_id);
                                                            const isOptedOut = groupForm.opted_out_client_ids.includes(client.client_id);
                                                            return (
                                                                <tr key={client.client_id} className={isOptedOut ? 'table-warning' : ''}>
                                                                    <td>
                                                                        <input
                                                                            className="form-check-input"
                                                                            type="checkbox"
                                                                            id={`group_client_${client.client_id}`}
                                                                            checked={isSelected}
                                                                            onChange={() => toggleClientSelection(client.client_id)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <label 
                                                                            className={`form-check-label ${isOptedOut ? 'text-decoration-line-through' : ''}`} 
                                                                            htmlFor={`group_client_${client.client_id}`}
                                                                        >
                                                                            {client.client_name}
                                                                        </label>
                                                                    </td>
                                                                    <td>
                                                                        {isSelected && (
                                                                            <input
                                                                                className="form-check-input"
                                                                                type="checkbox"
                                                                                id={`group_optout_${client.client_id}`}
                                                                                checked={isOptedOut}
                                                                                onChange={() => toggleOptOut(client.client_id)}
                                                                                disabled={initialOptedOutClientIds.includes(client.client_id)}
                                                                                title={initialOptedOutClientIds.includes(client.client_id) ? "Client has opted out (cannot be opted back in)" : "Mark as opted out (will not receive messages)"}
                                                                            />
                                                                        )}
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                        <div className="form-text">
                                            Opted-out clients will remain in the group but won't receive messages
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                                        {submitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2"></span>
                                                {editingGroup ? 'Updating...' : 'Creating...'}
                                            </>
                                        ) : (
                                            editingGroup ? 'Update' : 'Create'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Confirm Delete</h5>
                                <button type="button" className="btn-close" onClick={closeModals}></button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    Are you sure you want to delete this {deleteType === 'masstext' ? 'mass text' : 'group'}?
                                </p>
                                {deleteType === 'masstext' && selectedItem && (
                                    <div className="alert alert-warning">
                                        <strong>Message:</strong> {selectedItem.message.substring(0, 100)}...
                                        <br />
                                        <strong>Status:</strong> {selectedItem.status}
                                        {selectedItem.status === 'sent' && (
                                            <>
                                                <br />
                                                <strong>Sent:</strong> {selectedItem.success_count}/{selectedItem.total_recipients} recipients
                                            </>
                                        )}
                                    </div>
                                )}
                                {deleteType === 'group' && selectedItem && (
                                    <div className="alert alert-warning">
                                        <strong>Group:</strong> {selectedItem.group_name}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeModals}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={deleteType === 'masstext' ? handleDeleteMassText : handleDeleteGroup}
                                    disabled={submitting}
                                >
                                    {submitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MassText;
